import { useState, useEffect } from "react";
import { Canvas } from "fabric";
import { cn } from "@/lib/utils";
import { useAnalysis } from "@/hooks/use-analysis";
import { AnalysisContent } from "./AnalysisContent";
import { useToolUsage } from "@/hooks/use-tool-usage";
import { useToast } from "@/hooks/use-toast";
import { usePremiumFeatures } from "@/hooks/use-premium-features";
import { AnalysisHeader } from "./AnalysisHeader";
import { AnalysisError } from "./AnalysisError";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGuideMode } from "@/hooks/use-guide-mode";
import { GuideMode } from "./guide/GuideMode";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, BookOpen } from "lucide-react";
import { CustomCanvas } from "@/types/fabric";

const getStoredActiveTab = (): string => {
  try {
    return localStorage.getItem('analysis_active_tab') || 'analysis';
  } catch (e) {
    return 'analysis';
  }
};

const setStoredActiveTab = (tab: string): void => {
  try {
    localStorage.setItem('analysis_active_tab', tab);
  } catch (e) {
    console.error("Error storing active tab in localStorage:", e);
  }
};

interface SecondOpinionProps {
  canvas: Canvas | null;
  activeTool?: string | null;
  currentDrawingId: string | null;
  onResetAnalysis?: (resetFn: () => void) => void;
  onToolChange?: (tool: string) => void;
  onOpen?: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const SecondOpinion = ({ 
  canvas, 
  activeTool, 
  currentDrawingId, 
  onResetAnalysis,
  onToolChange,
  isOpen,
  setIsOpen
}: SecondOpinionProps) => {
  const { toast } = useToast();
  const { isAuthenticated, handlePremiumFeature } = usePremiumFeatures();
  const [activeTab, setActiveTab] = useState<string>(getStoredActiveTab());
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const {
    isAnalyzing,
    loadingMessage,
    analysisResult,
    activeScenario,
    analyzeDrawing,
    error,
    resetError,
    resetAnalysis,
    optimisticResult,
    setOptimisticResult
  } = useAnalysis(canvas, currentDrawingId);

  const {
    isGuideMode,
    toggleGuideMode
  } = useGuideMode(activeScenario);

  useEffect(() => {
    if (onResetAnalysis) {
      onResetAnalysis(resetAnalysis);
    }
  }, [resetAnalysis, onResetAnalysis]);

  const { usedTools } = useToolUsage(canvas, activeTool);

  useEffect(() => {
    if (analysisResult) {
      console.log('Analysis result available, ensuring panel is open:', analysisResult.substring(0, 20) + '...');
      setIsOpen(true);
    }
  }, [analysisResult, setIsOpen]);

  useEffect(() => {
    if (canvas) {
      const canvasData = (canvas as CustomCanvas).data;
      if (canvasData?.analysisText) {
        console.log('Found analysis data in canvas on panel mount, ensuring panel is open');
        setIsOpen(true);
      }
    }
  }, [canvas, currentDrawingId, setIsOpen]);

  useEffect(() => {
    try {
      localStorage.setItem('analysis_panel_open', isOpen.toString());
    } catch (e) {
      console.error("Error storing panel open state in localStorage:", e);
    }
  }, [isOpen]);

  const handleRerunAnalysis = async () => {
    if (activeScenario) {
      console.log('Reanalyze button clicked, triggering new analysis for scenario:', activeScenario);
      
      try {
        resetError();
        // Use optimistic result to show intermediate state
        setOptimisticResult("Analyzing your drawing...");
        await analyzeDrawing(activeScenario);
        
        toast({
          title: "Analysis completed",
          description: "Your drawing has been analyzed successfully.",
        });
      } catch (err) {
        console.error('Error during reanalysis:', err);
        toast({
          title: "Analysis failed",
          description: "There was an error analyzing your drawing. Please try again.",
          variant: "destructive",
        });
      } finally {
        // Clear optimistic result when done
        setOptimisticResult(null);
      }
    } else {
      console.error('Cannot reanalyze: No active scenario');
      toast({
        title: "No scenario",
        description: "Please add a scenario before analyzing.",
        variant: "destructive",
      });
    }
  };

  const handleRetry = async () => {
    if (activeScenario) {
      try {
        resetError();
        setOptimisticResult("Retrying analysis...");
        await analyzeDrawing(activeScenario);
        toast({
          title: "Analysis successful",
          description: "Successfully re-analyzed your drawing.",
        });
      } catch (err) {
        toast({
          title: "Retry failed",
          description: "Failed to retry analysis. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setOptimisticResult(null);
      }
    }
  };

  const handleAddClick = () => {
    if (onToolChange) {
      console.log('SecondOpinion: handleAddClick - Changing tool to "add"');
      onToolChange("add");
    }
  };

  const handleToolSelect = (tool: string) => {
    if (onToolChange) {
      onToolChange(tool);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setStoredActiveTab(value);
    
    if ((value === "guide" && !isGuideMode) || (value === "analysis" && isGuideMode)) {
      toggleGuideMode();
    }
  };

  useEffect(() => {
    const newTab = isGuideMode ? "guide" : "analysis";
    setActiveTab(newTab);
    setStoredActiveTab(newTab);
  }, [isGuideMode]);

  const handleAnalyzeFromGuide = () => {
    handleTabChange("analysis");
    handleRerunAnalysis();
  };

  if (!isOpen) return null;

  const containerHeight = isExpanded ? "h-[650px]" : "h-[382px]";

  return (
    <div 
      className={cn(
        "fixed z-50 right-6 bottom-[60px]",
        "w-[400px]",
        containerHeight,
        "rounded-xl",
        "bg-white/40 backdrop-blur-md",
        "border border-gray-200/40",
        "transition-all duration-300 animate-in fade-in-0 zoom-in-95",
        "shadow-lg",
      )}
    >
      <div className="flex flex-col h-full rounded-xl overflow-hidden">
        <AnalysisHeader
          isAnalyzing={isAnalyzing}
          hasResult={!!analysisResult}
          isMinimized={false}
          onRerunAnalysis={handleRerunAnalysis}
          onClose={() => setIsOpen(false)}
          onToggleMinimize={() => {}}
          activeScenario={activeScenario}
          isGuideMode={isGuideMode}
          isExpanded={isExpanded}
          onToggleExpand={toggleExpanded}
        />

        {activeScenario && (
          <div className="px-5 pt-3 pb-1">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid grid-cols-2 w-full bg-gray-100/30 backdrop-blur-sm rounded-full p-1 h-9">
                <TabsTrigger 
                  value="analysis"
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-full",
                    "text-indigo-700 text-xs font-medium h-full",
                    "data-[state=active]:bg-white/70 data-[state=active]:shadow-sm",
                    "data-[state=active]:border data-[state=active]:border-gray-200/40",
                    "data-[state=inactive]:text-indigo-500/80 data-[state=inactive]:hover:text-indigo-600",
                    "transition-all duration-200"
                  )}
                >
                  <Brain className="h-3.5 w-3.5" />
                  <span>Analysis</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="guide"
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-full",
                    "text-indigo-700 text-xs font-medium h-full",
                    "data-[state=active]:bg-white/70 data-[state=active]:shadow-sm",
                    "data-[state=active]:border data-[state=active]:border-gray-200/40",
                    "data-[state=inactive]:text-indigo-500/80 data-[state=inactive]:hover:text-indigo-600",
                    "transition-all duration-200"
                  )}
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Guide</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        <ScrollArea className="flex-1 px-1 overflow-y-auto">
          <div className={cn(
            "p-4 bg-transparent h-full",
            isExpanded ? "pb-6" : "pb-4"
          )}>
            <div className={cn(
              "space-y-3", 
              isExpanded && "md:space-y-4"
            )}>
              {activeTab === "guide" && activeScenario ? (
                <GuideMode 
                  currentScenario={activeScenario}
                  usedTools={usedTools}
                  onExit={() => handleTabChange("analysis")}
                  onToolSelect={handleToolSelect}
                  isExpanded={isExpanded}
                  onAnalyze={handleAnalyzeFromGuide}
                />
              ) : (
                <>
                  {error && <AnalysisError error={error} onRetry={handleRetry} />}
                  
                  <AnalysisContent
                    activeScenario={activeScenario}
                    isAnalyzing={isAnalyzing}
                    loadingMessage={loadingMessage}
                    analysisResult={optimisticResult || analysisResult}
                    usedTools={usedTools}
                    onAnalyze={handleRerunAnalysis}
                    error={error}
                    onAddClick={handleAddClick}
                    onStartGuide={() => handleTabChange("guide")}
                    isExpanded={isExpanded}
                  />
                </>
              )}
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
